import { format as formatDate } from 'date-fns'

/**
 * Resolves a column reference to a column definition
 *
 * @param {string|Object} ref - Column id string, source string, or full column definition object
 * @param {Array} columns - Array of column definitions to search
 * @returns {Object} The resolved column definition
 */
export function resolveColumnRef(ref, columns = []) {
  // If ref is already a full column definition object, return it
  if (ref && typeof ref === 'object' && (ref.source || ref.id)) {
    // If it has an id but no source, try to find the source from columns
    if (ref.id && !ref.source) {
      const found = columns.find(c => c.id === ref.id)
      if (found) {
        return { ...found, ...ref }
      }
    }
    return ref
  }

  // If ref is a string, find matching column by id or source
  if (typeof ref === 'string') {
    const byId = columns.find(c => c.id === ref)
    if (byId) return byId

    const bySource = columns.find(c => c.source === ref)
    if (bySource) return bySource

    // If not found in columns, treat it as a direct source
    return { source: ref }
  }

  return ref
}

/**
 * Resolves a value from a row using a column definition
 *
 * @param {Object} row - The data row
 * @param {Object} column - Column definition with source, kind, format, etc.
 * @param {Object} dimensionInfo - Dimension lookup data (e.g., { clientId: [{ id: 1, name: 'Client A' }] })
 * @returns {*} The resolved value
 *
 * Column definition:
 * - id: optional identifier for referencing this column
 * - source: string (property name), dot notation (e.g., 'clientId.name'), or function (row, dimensionInfo) => value
 * - kind: 'date' | 'number' | 'percentage' | 'currency' | undefined
 * - dateFormat: date-fns format string (default: 'MMM d, yyyy')
 * - format: 'percentage' | 'currency' | 'number' | custom function
 * - decimalPlaces: number of decimal places for number formatting
 */
export function resolveValue(row, column, dimensionInfo = {}) {
  const source = column.source

  // If source is a function, call it with row and dimensionInfo
  if (typeof source === 'function') {
    return source(row, dimensionInfo)
  }

  let value
  if (typeof source === 'string' && source.includes('.')) {
    const [dimensionId, property] = source.split('.')
    const entityId = row[dimensionId]
    const entities = dimensionInfo[dimensionId] || []
    const entity = entities.find(e => e.id === entityId)
    value = entity?.[property] ?? entityId
  } else {
    value = row[source]
  }

  return value
}

/**
 * Formats a value according to column definition
 *
 * @param {*} value - The value to format
 * @param {Object} column - Column definition
 * @returns {string} Formatted value
 */
export function formatValue(value, column = {}) {
  if (value === null || value === undefined) {
    return ''
  }

  // Handle date formatting
  if (column.kind === 'date' && value) {
    return formatDate(new Date(value), column.dateFormat || 'MMM d, yyyy')
  }

  // Handle format option
  const format = column.format
  if (typeof format === 'function') {
    return format(value)
  }

  const decimalPlaces = column.decimalPlaces ?? 1

  if (format === 'percentage') {
    const numValue = typeof value === 'number' ? value : parseFloat(value)
    return `${(numValue * 100).toFixed(decimalPlaces)}%`
  }

  if (format === 'currency') {
    const numValue = typeof value === 'number' ? value : parseFloat(value)
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(numValue)
  }

  if (format === 'number' || column.kind === 'number') {
    const numValue = typeof value === 'number' ? value : parseFloat(value)
    return numValue.toLocaleString()
  }

  return value
}

/**
 * Resolves and formats a value in one step
 */
export function resolveAndFormat(row, column, dimensionInfo = {}) {
  const value = resolveValue(row, column, dimensionInfo)
  return formatValue(value, column)
}

/**
 * Creates Plotly-compatible tick format based on column format
 *
 * @param {Object} column - Column definition
 * @returns {Object} Plotly axis format options
 */
export function getPlotlyAxisFormat(column = {}) {
  const format = column.format

  if (format === 'percentage') {
    return {
      tickformat: '.1%',
      hoverformat: '.2%'
    }
  }

  if (format === 'currency') {
    return {
      tickprefix: '£',
      tickformat: ',.0f',
      hoverformat: '£,.2f'
    }
  }

  if (format === 'number' || column.kind === 'number') {
    return {
      tickformat: ',',
      hoverformat: ','
    }
  }

  return {}
}

/**
 * Groups data by a dimension for creating multiple traces
 *
 * @param {Array} data - Array of data rows
 * @param {Object} seriesColumn - Resolved column definition to group by
 * @param {Object} dimensionInfo - Dimension lookup data
 * @returns {Map} Map of series name -> array of rows
 */
export function groupDataBySeries(data, seriesColumn, dimensionInfo = {}) {
  if (!seriesColumn || !data || data.length === 0) {
    return new Map([['all', data]])
  }

  const groups = new Map()

  for (const row of data) {
    const seriesKey = resolveValue(row, seriesColumn, dimensionInfo)
    const seriesName = seriesColumn.label
      ? resolveAndFormat(row, seriesColumn, dimensionInfo)
      : String(seriesKey)

    if (!groups.has(seriesName)) {
      groups.set(seriesName, [])
    }
    groups.get(seriesName).push(row)
  }

  return groups
}

/**
 * Prepares data for a Plotly line chart with support for derived columns and series grouping
 *
 * @param {Object} options
 * @param {Array} options.data - Raw data array
 * @param {Object} options.dimensionInfo - Dimension lookup data
 * @param {Array} options.columns - Column definitions array
 * @param {string|Object} options.x - X-axis column reference (id, source string, or full definition)
 * @param {string|Object} options.y - Y-axis column reference (id, source string, or full definition)
 * @param {string|Object} options.seriesBy - Optional column reference to create series by
 * @returns {Object} { traces: Array, xAxisConfig: Object, yAxisConfig: Object }
 */
export function prepareLineChartData({ data, dimensionInfo = {}, columns = [], x, y, seriesBy }) {
  if (!data || data.length === 0) {
    return { traces: [], xAxisConfig: {}, yAxisConfig: {} }
  }

  // Resolve column references
  const xColumn = resolveColumnRef(x, columns)
  const yColumn = resolveColumnRef(y, columns)
  const seriesColumn = seriesBy ? resolveColumnRef(seriesBy, columns) : null

  const groups = groupDataBySeries(data, seriesColumn, dimensionInfo)
  const traces = []

  for (const [seriesName, seriesData] of groups) {
    // Sort by x value for proper line rendering
    const sortedData = [...seriesData].sort((a, b) => {
      const aVal = resolveValue(a, xColumn, dimensionInfo)
      const bVal = resolveValue(b, xColumn, dimensionInfo)
      if (aVal instanceof Date || typeof aVal === 'string' && !isNaN(Date.parse(aVal))) {
        return new Date(aVal) - new Date(bVal)
      }
      return aVal - bVal
    })

    const xValues = sortedData.map(row => resolveValue(row, xColumn, dimensionInfo))
    const yValues = sortedData.map(row => resolveValue(row, yColumn, dimensionInfo))

    // Create hover text with formatted values
    const hoverText = sortedData.map(row => {
      const xFormatted = formatValue(resolveValue(row, xColumn, dimensionInfo), xColumn)
      const yFormatted = formatValue(resolveValue(row, yColumn, dimensionInfo), yColumn)
      const parts = [
        xColumn.label ? `${xColumn.label}: ${xFormatted}` : xFormatted,
        yColumn.label ? `${yColumn.label}: ${yFormatted}` : yFormatted
      ]
      if (seriesColumn && seriesName !== 'all') {
        parts.unshift(seriesName)
      }
      return parts.join('<br>')
    })

    traces.push({
      x: xValues,
      y: yValues,
      type: 'scatter',
      mode: 'lines+markers',
      name: seriesName === 'all' ? (yColumn.label || 'Value') : seriesName,
      text: hoverText,
      hoverinfo: 'text'
    })
  }

  const xAxisConfig = {
    title: xColumn.label,
    ...getPlotlyAxisFormat(xColumn)
  }

  const yAxisConfig = {
    title: yColumn.label,
    ...getPlotlyAxisFormat(yColumn)
  }

  return { traces, xAxisConfig, yAxisConfig }
}
